import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Target, TrendingUp } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount).replace('MYR', 'RM');
};

export const ProgressSection = ({ stats, loading }) => {
  const { 
    total_pledged, 
    min_target, 
    max_target, 
    min_target_percentage, 
    max_target_percentage,
    is_min_achieved,
    is_nearing_cap 
  } = stats;

  // Calculate visual progress (capped at 100% for display)
  const minProgressWidth = Math.min(100, min_target_percentage);
  const maxProgressWidth = Math.min(100, max_target_percentage);

  // Determine progress bar color
  const getProgressColor = () => {
    if (!is_min_achieved) return 'bg-muted-foreground/30';
    if (is_nearing_cap) return 'bg-primary';
    return 'bg-success';
  };

  const getProgressGlow = () => {
    if (!is_min_achieved) return '';
    if (is_nearing_cap) return 'shadow-primary';
    return 'shadow-success';
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-xl bg-card">
        <CardContent className="p-8">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-6 w-full mb-6" />
          <div className="flex gap-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-32" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-xl bg-card">
      <CardContent className="p-6 sm:p-8">
        {/* Total Pledged Display */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">Total Pledged</p>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground">
              {formatCurrency(total_pledged)}
            </h2>
          </div>
          
          {/* Status badges */}
          <div className="flex flex-wrap gap-2">
            {is_min_achieved ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-success-light text-success text-sm font-medium">
                <Target className="h-3.5 w-3.5" />
                Minimum Target Achieved
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">
                <Target className="h-3.5 w-3.5" />
                In Progress
              </span>
            )}
            
            {is_nearing_cap && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium animate-pulse-soft">
                <TrendingUp className="h-3.5 w-3.5" />
                Limited Allocation
              </span>
            )}
          </div>
        </div>
        
        {/* Progress Bar - Maximum Target */}
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Progress to Maximum ({formatCurrency(max_target)})</span>
            <span className="font-medium text-foreground">{max_target_percentage.toFixed(1)}%</span>
          </div>
          
          <div className="relative h-4 bg-muted rounded-full overflow-hidden">
            {/* Min target marker */}
            <div 
              className="absolute top-0 bottom-0 w-0.5 bg-foreground/30 z-10"
              style={{ left: `${(min_target / max_target) * 100}%` }}
            />
            
            {/* Progress fill */}
            <div 
              className={`h-full rounded-full transition-all duration-1000 ease-out ${getProgressColor()} ${getProgressGlow()}`}
              style={{ width: `${maxProgressWidth}%` }}
            />
          </div>
          
          {/* Labels */}
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>RM 0</span>
            <span className="absolute" style={{ left: `${(min_target / max_target) * 100}%`, transform: 'translateX(-50%)' }}>
              Min: {formatCurrency(min_target)}
            </span>
            <span>Max: {formatCurrency(max_target)}</span>
          </div>
        </div>
        
        {/* Secondary stats */}
        <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-border">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">of minimum target</p>
            <p className="text-lg font-semibold text-foreground">{min_target_percentage.toFixed(1)}%</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground mb-0.5">of maximum allocation filled</p>
            <p className="text-lg font-semibold text-foreground">{max_target_percentage.toFixed(1)}%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProgressSection;
